import React, { useEffect, useState } from "react";
import {
  AlertTriangle,
  BarChart3,
  Calendar,
  CheckCircle,
  Info,
  Star,
  Target,
  TrendingDown,
  RefreshCw,
  TrendingUp,
  Trophy,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BuyPricingIntelligenceResponse,
  buyAdminApi,
} from "../../../services/buyAdminApi";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./PricingIntelligence.module.css";

const emptyPricing: BuyPricingIntelligenceResponse = {
  window: { start: "", end: "" },
  pricingData: {
    totalMarkup: 0,
    avgMarkupDollar: 0,
    avgMarkupPercent: 0,
    avgCompetitorDiff: 0,
    competitivenessScore: 0,
    totalTransactions: 0,
    totalSavingsToMembers: 0,
    totalTickets: 0,
    savingsShare: 0,
    markupShare: 0,
  },
  markupByLeague: [],
  marginTrendData: [],
  insights: {
    bestLeague: "top categories",
    highestMarkupLeague: "high-markup events",
    lowestCompetitiveLeague: "lower-rate categories",
  },
};

const formatCurrency = (value: number | null | undefined, digits = 0) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const formatSignedCurrency = (value: number | null | undefined) => {
  const amount = Number(value || 0);
  const formatted = formatCurrency(Math.abs(amount), 2);
  return amount < 0 ? `-${formatted}` : formatted;
};

const FilterButton = ({ label }: { label: string }) => (
  <button className={styles.filterButton} type="button">
    <Calendar size={14} />
    {label}
  </button>
);

const BuyAdminPricingIntelligence = () => {
  const [data, setData] = useState<BuyPricingIntelligenceResponse>(emptyPricing);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const loadPricing = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await buyAdminApi.getPricingIntelligence();
      setData(response);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load pricing intelligence.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPricing();
  }, []);

  const pricingData = data.pricingData;
  const savingsAdvantage = pricingData.totalMarkup > 0
    ? Math.max(0, Math.round((pricingData.totalSavingsToMembers / pricingData.totalMarkup) * 100 - 100))
    : 0;
  const circumference = 352;
  const scoreDash = Math.max(0, Math.min(circumference, pricingData.competitivenessScore * 3.52));

  return (
    <div className={`${styles.page} buy-admin-theme-scope`} data-testid="pricing-intelligence-page">
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1>Pricing Intelligence</h1>
          <p>Markup analysis vs competitors (Ticketmaster, StubHub, SeatGeek)</p>
        </div>
        <div className={`${styles.headerActions} buy-admin-theme-actions`}>
          <FilterButton label="Filter All" />
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => void loadPricing()}
            disabled={loading}
            aria-label="Refresh pricing intelligence"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? styles.spin : undefined} />
          </button>
          <BuyAdminThemeToggle iconOnly />
        </div>
      </div>

      {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

      <section className={`${styles.card} ${styles.kpiCard}`}>
        <div className={styles.cardHeader}>
          <h2><Target size={20} /> Key Pricing Metrics</h2>
          <FilterButton label={loading ? "Loading" : "Filter"} />
        </div>
        <div className={styles.kpiGrid}>
          <div className={`${styles.metricTile} ${styles.purpleTile}`}>
            <span>Total Markup</span>
            <strong>{formatCurrency(pricingData.totalMarkup)}</strong>
            <em>{pricingData.totalTransactions.toLocaleString()} transactions</em>
          </div>
          <div className={`${styles.metricTile} ${styles.blueTile}`}>
            <span>Avg Markup ($)</span>
            <strong>{formatCurrency(pricingData.avgMarkupDollar, 2)}</strong>
            <em>per ticket</em>
          </div>
          <div className={`${styles.metricTile} ${styles.indigoTile}`}>
            <span>Avg Markup (%)</span>
            <strong>{pricingData.avgMarkupPercent.toFixed(1)}%</strong>
            <em>margin rate</em>
          </div>
          <div className={`${styles.metricTile} ${styles.emeraldTile}`}>
            <span>Avg vs Competitors</span>
            <strong>{formatSignedCurrency(pricingData.avgCompetitorDiff)}</strong>
            <em>cheaper on average</em>
          </div>
          <div className={`${styles.metricTile} ${styles.amberTile}`}>
            <span>Competitiveness</span>
            <strong>{pricingData.competitivenessScore.toFixed(0)}%</strong>
            <em>lowest price rate</em>
          </div>
        </div>
      </section>

      <div className={styles.twoColumnGrid}>
        <section className={styles.card}>
          <div className={styles.smallHeader}>
            <h2><BarChart3 size={16} /> Savings vs Markup Ratio</h2>
          </div>
          <div className={styles.ratioRow}>
            <div className={styles.ratioSide}>
              <div className={styles.ratioMeta}>
                <span>Member Savings</span>
                <strong className={styles.greenText}>{formatCurrency(pricingData.totalSavingsToMembers)}</strong>
              </div>
              <div className={styles.ratioTrack}>
                <div className={styles.savingsBar} style={{ width: `${Math.max(2, pricingData.savingsShare)}%` }} />
              </div>
            </div>
            <div className={styles.ratioColon}>:</div>
            <div className={styles.ratioSide}>
              <div className={styles.ratioMeta}>
                <span>Your Markup</span>
                <strong className={styles.purpleText}>{formatCurrency(pricingData.totalMarkup)}</strong>
              </div>
              <div className={styles.ratioTrack}>
                <div className={styles.markupBar} style={{ width: `${Math.max(2, pricingData.markupShare)}%` }} />
              </div>
            </div>
          </div>
          <div className={styles.ratioSummary}>
            Members save <strong>{savingsAdvantage}% more</strong> than your margin
          </div>
        </section>

        <section className={styles.card}>
          <div className={styles.smallHeader}>
            <h2><Trophy size={16} /> Price Competitiveness Score</h2>
          </div>
          <div className={styles.scoreLayout}>
            <div className={styles.scoreRing}>
              <svg viewBox="0 0 128 128">
                <circle cx="64" cy="64" r="56" fill="none" stroke="var(--buy-chart-grid)" strokeWidth="12" />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="12"
                  strokeDasharray={`${scoreDash} ${circumference}`}
                  strokeLinecap="round"
                />
              </svg>
              <span>{pricingData.competitivenessScore.toFixed(0)}%</span>
            </div>
            <div className={styles.scoreList}>
              <p><CheckCircle size={16} /> <strong>{pricingData.competitivenessScore.toFixed(0)}%</strong> of tickets priced lowest</p>
              <p><TrendingDown size={16} /> <strong>{formatSignedCurrency(pricingData.avgCompetitorDiff)}</strong> avg below competitors</p>
              <p><Star size={16} /> Best rates on <strong>{data.insights.bestLeague}</strong></p>
            </div>
          </div>
        </section>
      </div>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h2><BarChart3 size={20} /> Markup Distribution by League/Event Type</h2>
          <FilterButton label="Filter" />
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>League</th>
                <th>Avg Markup</th>
                <th>Vs Competitors</th>
                <th>Transactions</th>
                <th>Competitive Rate</th>
              </tr>
            </thead>
            <tbody>
              {data.markupByLeague.map((row) => (
                <tr key={row.league}>
                  <td>{row.league}</td>
                  <td className={styles.purpleText}>{formatCurrency(row.avgMarkup, 2)}</td>
                  <td className={styles.greenText}>{formatSignedCurrency(row.avgCompDiff)}</td>
                  <td>{row.transactions.toLocaleString()}</td>
                  <td><span className={row.competitiveRate >= 90 ? styles.badgeGreen : row.competitiveRate >= 85 ? styles.badgeBlue : styles.badgeAmber}>{row.competitiveRate.toFixed(0)}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h2><TrendingUp size={20} /> Margin Trend Over Time</h2>
          <FilterButton label="Filter" />
        </div>
        <div className={styles.chartPanel}>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.marginTrendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--buy-chart-grid)" />
              <XAxis dataKey="month" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Area yAxisId="left" type="monotone" dataKey="avgMarkup" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} name="Avg Markup ($)" />
              <Line yAxisId="right" type="monotone" dataKey="volume" stroke="#10b981" name="Transaction Volume" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className={`${styles.card} ${styles.insightsCard}`}>
        <h3><Info size={20} /> Pricing Insights Summary</h3>
        <div className={styles.insightsGrid}>
          <div>
            <h4 className={styles.greenText}><CheckCircle size={16} /> Strengths</h4>
            <p>• {data.insights.bestLeague} has the strongest competitive rate</p>
            <p>• Members save {savingsAdvantage}% more than your margin</p>
            <p>• {pricingData.competitivenessScore.toFixed(0)}% of tickets are priced lowest</p>
          </div>
          <div>
            <h4 className={styles.amberText}><AlertTriangle size={16} /> Opportunities</h4>
            <p>• {data.insights.lowestCompetitiveLeague} has room to improve</p>
            <p>• {data.insights.highestMarkupLeague} markup is highest - monitor closely</p>
            <p>• Consider dynamic pricing for events</p>
          </div>
          <div>
            <h4 className={styles.blueText}><TrendingUp size={16} /> Trends</h4>
            <p>• Avg markup is {formatCurrency(pricingData.avgMarkupDollar, 2)} per ticket</p>
            <p>• Transaction volume is {pricingData.totalTransactions.toLocaleString()} in this window</p>
            <p>• Currently {formatSignedCurrency(pricingData.avgCompetitorDiff)} below competitors</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BuyAdminPricingIntelligence;
